// vuex
import { mapState, mapMutations } from 'vuex'

// types
import {
  USER_MODULE_NAME,
  WARRANTY_MODULE_NAME,
  WARRANTY_CLEAR_MODULE
} from '@/store/types'

// constants
import { TAB_NAV, MA_WARRANTY_WATER_MOBILIER } from '@/constants'

// components
import maTabNav from '@/components/tabnav'
import maWarrantyRef from './components/warrantyRef'
import maWarrantyProps from './components/warrantyProps'
import maWarrantySticky from './components/warrantySticky'
import maEventbus from '@/components/eventbus'
import maModal from '@/components/modal'
import maInput from '@/components/input'

/**
 * @module
 * @name warrantyLayout
 */
export default {
  name: 'warrantyLayout',

  /**
   * @property {Number} warrantyLayoutKey Child components uses a key that's
   *                                      modified on new sinister event.
   */
  data: function() {
    return {
      warrantyLayoutKey: 0
    }
  },

  components: {
    maTabNav,
    maWarrantyRef,
    maWarrantyProps,
    maWarrantySticky,
    maEventbus,
    maModal,
    maInput
  },

  /**
   * @property {Number} warrantyid Warranty ID from route meta.
   * @property {Boolean} sinisterHasDamages Returns true if sticky has summary
   *                                        elements.
   * @property {Boolean} shouldShowRefs Hide warrantyRef component if FNOLFlow
   *                                    set it.
   */
  computed: {
    ...mapState(USER_MODULE_NAME, {
      stateUserRoutes: 'routes'
    }),
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantySinisterSummary: 'sinisterSummary',
      stateWarrantyCreated: 'created'
    }),
    warrantyid: function() {
      return this.$route.meta.warrantyid || ''
    },
    sinisterHasDamages: function() {
      return (
        this.stateWarrantySinisterSummary &&
        this.stateWarrantySinisterSummary.length > 0
      )
    },
    shouldShowRefs: function() {
      return !this.FNOLFlow.params.hideRef
    },
    isMobilierRoute() {
      return this.$route.name === MA_WARRANTY_WATER_MOBILIER
    }
  },

  methods: {
    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyClearModule: WARRANTY_CLEAR_MODULE
    }),

    /**
     * @function
     * @name matchWarrantyGroup
     * @param {Number} warrantyid Warranty ID.
     * @returns {Boolean} Returns true if param and compute warranty id are the same.
     */
    matchWarrantyGroup: function(warrantyid) {
      return warrantyid === this.warrantyid
    },

    /**
     * @function
     * @name parseRoutes
     * @param {Array} routes Array of route objects.
     * @description Fill dataUserTabs with routes that match route warrantyId.
     */
    parseRoutes: function(routes) {
      routes.forEach(route => {
        // add
        if (
          this.matchWarrantyGroup(route.meta.warrantyid) &&
          route.meta.type === TAB_NAV &&
          route.name
        ) {
          this.dataUserTabs.push(route)
        }
        // has childrens? parse them
        if (route.children) {
          this.parseRoutes(route.children)
        }
      })
    },

    /**
     * @function
     * @name setTabs
     * @description Init process of setting up tabs, and then redirect to first
     *              one.
     */
    setTabs: function() {
      this.dataUserTabs = []
      this.parseRoutes(this.stateUserRoutes)

      // if no tab selected, redirect to another one
      if (this.$route.meta.warrantyContainer) {
        this.$router.replace({ name: this.dataUserTabs[0].name })
      }
    },

    /**
     * @function
     * @name handleNewSinister
     * @description Shows confirm modal to create a new sinister.
     */
    handleNewSinister: function() {
      this.$refs.newSinisterConfirmationModal.$data.showModal = true
    },

    /**
     * @function
     * @name confirmNewSinister
     * @description If new sinister is confirmed, warrantyLayout is increased to
     *              refresh the components that contain it.
     */
    confirmNewSinister: function() {
      this.warrantyLayoutKey = this.warrantyLayoutKey + 1
      this.$refs.newSinisterConfirmationModal.$data.showModal = false
      this.mutWarrantyClearModule()
      if (this.FNOLFlow.isExecuting) {
        this.FNOLFlow.params.refSin = null
        this.FNOLFlow.params.refEvt = null
      } else {
        this.$router.replace({ name: 'warranty', params: {} })
      }
    },

    /**
     * @function
     * @name cancelNewSinister Hide confirm modal to create a new sinister.
     */
    cancelNewSinister: function() {
      this.$refs.newSinisterConfirmationModal.$data.showModal = false
    },

    /**
     * @function
     * @name isWarrantyRoute
     * @param {route} route routeObject
     * @returns !undefined if warrantyid exists in the meta route.
     */
    isWarrantyRoute: function(route) {
      return route.meta.warrantyid
    }
  },

  /**
   * @property {function} $route If route changes and old warranty id differs from
   *                             the new one, it set tabs according to new
   *                             warranty group.
   */
  watch: {
    $route: function(oldRoute, newRoute) {
      const {
        meta: { warrantyid: oldWarrantyId }
      } = oldRoute
      const {
        meta: { warrantyid: newWarrantyId }
      } = newRoute

      if (oldWarrantyId !== newWarrantyId) {
        this.setTabs()
      }
    }
  },

  /**
   * @function
   * @name beforeMount
   * @description Set tabs to navigate in the warranty group view.
   */
  beforeMount: function() {
    this.setTabs()
  }

  /**
   * @function
   * @name beforeCreate
   * @description Register warranty module if it's not registered yet.
   */
  /* beforeCreate: function () {
    if (!(WARRANTY_MODULE_NAME in this.$store.state)) {
      this.$store.registerModule(WARRANTY_MODULE_NAME, warrantyModule)
    }
  } */
}
